import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/contexts/AuthContext';
import OGCreatorsGame from '../../creative/components/OGCreatorsGame';
import PlayerShowdown from './PlayerShowdown';
import { getGameById } from '../models/GameRegistry';
import { gameAPI } from '../services/GameAPI';
import { subscribeToMidi } from '../services/webMidi';
import VirtualPianoKeyboard from './VirtualPianoKeyboard';
import AchievementNotification from '../../../shared/ui/AchievementNotification';
import SongNamingModal from './SongNamingModal';
import { auth, db } from '../../../shared/storage/firebase';
import { doc, increment, updateDoc } from 'firebase/firestore';

const FullScreenGamePlayer: React.FC = () => {
  const { gameId: gameIdParam } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { userData, updateUserData } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const autoDailyAwardedForGameIdRef = useRef<string | null>(null);
  const dailyTimeTimerRef = useRef<number | null>(null);
  const midiUnsubscribeRef = useRef<(() => void) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [currentAchievement, setCurrentAchievement] = useState<any>(null);
  const [songNamingData, setSongNamingData] = useState<{ eventId: string; audioUrl?: string } | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [viewport, setViewport] = useState<{ w: number; h: number }>({
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  const gameId = (gameIdParam || '').toString();
  const game = useMemo(() => (gameId ? getGameById(gameId) : null), [gameId]);

  const isOgCreators = gameId === 'og-creators';
  const isPlayerShowdown = gameId === 'player-showdown';
  const isMidiEnabledGame = gameId === 'key-smashers';
  const [showInlinePiano, setShowInlinePiano] = useState(false);

  // Keep latest userData in a ref so achievement handler remains valid
  const userDataRef = useRef(userData);
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // The player owns the whole viewport. Prevent the app shell behind it from
  // creating a second scrollbar while a game is open.
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    // Simple session timer (resets per game).
    setElapsedSeconds(0);
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [gameId]);

  // For Key Smashers, auto-open the inline piano on initial mount.
  useEffect(() => {
    if (!isMidiEnabledGame) {
      setShowInlinePiano(false);
      return;
    }
    setShowInlinePiano(true);
  }, [isMidiEnabledGame]);

  // Song naming modal support (Layout isn't mounted on /play/*, but GameAPI.saveSong
  // can pause awaiting user naming via songNeedsNaming -> songNamed events).
  useEffect(() => {
    const handleSongNeedsNaming = (event: CustomEvent) => {
      const detail: any = (event as any)?.detail || {};
      if (!detail?.eventId) return;
      setSongNamingData({ eventId: detail.eventId, audioUrl: detail.audioUrl });
    };

    const eventHandler = (event: Event) => handleSongNeedsNaming(event as CustomEvent);
    window.addEventListener('songNeedsNaming', eventHandler);

    return () => {
      window.removeEventListener('songNeedsNaming', eventHandler);
    };
  }, []);

  const handleSongNameSave = (songName: string) => {
    if (!songNamingData) return;
    window.dispatchEvent(
      new CustomEvent('songNamed', {
        detail: { eventId: songNamingData.eventId, songName },
      })
    );
    setSongNamingData(null);
  };

  const handleSongNameSkip = () => {
    if (!songNamingData) return;
    window.dispatchEvent(
      new CustomEvent('songNamed', {
        detail: { eventId: songNamingData.eventId, songName: 'My Song' },
      })
    );
    setSongNamingData(null);
  };

  useEffect(() => {
    // Full-screen route doesn't mount App Layout, but achievements still need to:
    // - show the toast
    // - increment/persist museCash
    // Reuse the same global dedupe keys as Layout so we don't double-award in edge cases.
    const w = window as any;
    const PROCESSED_KEY = '__ogProcessedAchievementIds';

    if (!w[PROCESSED_KEY]) {
      w[PROCESSED_KEY] = new Set<string>();
    }
    const processedIds: Set<string> = w[PROCESSED_KEY];

    const handleAchievementUnlock = async (event: CustomEvent) => {
      const detail: any = (event as any)?.detail || {};
      const achievement = detail.achievement || null;
      const eventId = String(detail.eventId || '').trim();
      const rewardNum = Number((detail.reward ?? achievement?.museCashReward ?? 0));

      if (achievement) setCurrentAchievement(achievement);

      if (!Number.isFinite(rewardNum) || rewardNum <= 0) return;

      // Dedupe by stable eventId if provided (matches Layout behavior)
      if (eventId) {
        if (processedIds.has(eventId)) return;
        processedIds.add(eventId);
        try {
          const ssKey = `og_achv_${eventId}`;
          if (sessionStorage.getItem(ssKey) === '1') return;
          sessionStorage.setItem(ssKey, '1');
        } catch (_) {
          // ignore storage errors
        }
      }

      // Optimistically bump local cached userData for snappier UI after exiting the game.
      const latestUser = userDataRef.current;
      if (latestUser) {
        updateUserData({ museCash: (latestUser.museCash || 0) + rewardNum });
      }

      // Persist to Firestore with atomic increment (avoid clobbering concurrent writers)
      try {
        if (auth.currentUser && latestUser) {
          const userRef = doc(db, 'users', latestUser.id);
          await updateDoc(userRef, { museCash: increment(rewardNum) });
        }
      } catch (e) {
        console.warn('[FullScreenGamePlayer] Failed to persist museCash increment', e);
      }
    };

    const eventHandler = (event: Event) => void handleAchievementUnlock(event as CustomEvent);
    window.addEventListener('achievementUnlocked', eventHandler);
    return () => window.removeEventListener('achievementUnlocked', eventHandler);
  }, []);

  useEffect(() => {
    // Award Daily Practice *after* the full-screen achievement listener is mounted,
    // so the toast can render over the game while it loads.
    if (!gameId) return;
    if (!game) return;

    // Don't auto-award for truly external URLs or missing URLs (except OG Creators).
    if (!isOgCreators && !isPlayerShowdown && !game.gameUrl) return;
    if (autoDailyAwardedForGameIdRef.current === gameId) return;
    autoDailyAwardedForGameIdRef.current = gameId;

    // Fire-and-forget; the event will drive the toast.
    (async () => {
      try {
        await gameAPI.awardAchievement(gameId, 'daily-play');
      } catch (e) {
        console.warn('[FullScreenGamePlayer] Failed to auto-award daily-play', e);
      }
    })();
  }, [game, gameId, isOgCreators, isPlayerShowdown]);

  const setupUnityMidiBridge = useCallback(() => {
    if (!isMidiEnabledGame) return;
    if (!iframeRef.current) return;

    let attempts = 0;
    const maxAttempts = 20;
    const intervalMs = 500;

    const intervalId = window.setInterval(() => {
      attempts += 1;

      const currentIframe = iframeRef.current;
      if (!currentIframe) {
        window.clearInterval(intervalId);
        return;
      }

      const win = currentIframe.contentWindow as (Window & { unityInstance?: any }) | null;
      if (win && win.unityInstance && typeof win.unityInstance.SendMessage === 'function') {
        window.clearInterval(intervalId);

        if (midiUnsubscribeRef.current) {
          midiUnsubscribeRef.current();
          midiUnsubscribeRef.current = null;
        }

        midiUnsubscribeRef.current = subscribeToMidi(({ status, note, velocity }) => {
          try {
            // Normalize MIDI status to Channel 1 (0) for Unity WebGL compatibility
            // Note On: 0x90 - 0x9F -> 0x90 (144)
            // Note Off: 0x80 - 0x8F -> 0x80 (128)
            let normalizedStatus = status;
            if (status >= 144 && status <= 159) {
              normalizedStatus = 144;
            } else if (status >= 128 && status <= 143) {
              normalizedStatus = 128;
            }

            const dataString = `${normalizedStatus},${note},${velocity}`;
            console.log('[FullScreenGamePlayer] MIDI → Unity', {
              raw: { status, note, velocity },
              normalizedStatus,
              dataString,
            });
            win.unityInstance.SendMessage('MIDIListener', 'ReceiveMIDIMessage', dataString);
            win.unityInstance.SendMessage('MidiControl', 'ReceiveMIDIMessage', dataString);
          } catch (error) {
            console.warn('[FullScreenGamePlayer] Failed to send MIDI to Unity:', error);
          }
        });

        console.log('[FullScreenGamePlayer] Web MIDI bridge initialized for key-smashers.');
      } else if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        console.warn('[FullScreenGamePlayer] Timed out waiting for Unity instance for MIDI bridge.');
      }
    }, intervalMs);
  }, [isMidiEnabledGame]);

  useEffect(() => {
    // Cleanup MIDI subscription on unmount / game change.
    return () => {
      if (midiUnsubscribeRef.current) {
        midiUnsubscribeRef.current();
        midiUnsubscribeRef.current = null;
      }
    };
  }, [gameId]);

  useEffect(() => {
    // Daily Achievement parity with old GameContainer: Play for 5 minutes
    // (The actual daily logic/dedupe is enforced inside GameAPI.awardAchievement.)
    if (!gameId) return;
    if (!game) return;

    // Don't auto-award for truly external URLs or missing URLs (except OG Creators).
    if (!isOgCreators && !isPlayerShowdown && !game.gameUrl) return;

    // Clear any previous timer when switching games
    if (dailyTimeTimerRef.current) {
      window.clearTimeout(dailyTimeTimerRef.current);
      dailyTimeTimerRef.current = null;
    }

    const PLAY_TIME_THRESHOLD_MS = 5 * 60 * 1000;
    dailyTimeTimerRef.current = window.setTimeout(async () => {
      try {
        await gameAPI.awardAchievement(gameId, 'daily-time-5m');
      } catch (e) {
        console.warn('[FullScreenGamePlayer] Failed to auto-award daily-time-5m', e);
      }
    }, PLAY_TIME_THRESHOLD_MS);

    return () => {
      if (dailyTimeTimerRef.current) {
        window.clearTimeout(dailyTimeTimerRef.current);
        dailyTimeTimerRef.current = null;
      }
    };
  }, [game, gameId, isOgCreators, isPlayerShowdown]);

  useEffect(() => {
    if (!gameId || !game) {
      setError('Game not found.');
      return;
    }

    // OG Creators is an internal React game (no iframe URL).
    if (!isOgCreators && !isPlayerShowdown && !game.gameUrl) {
      setError('Game URL missing.');
      return;
    }

    let mounted = true;

    const normalizeSongPayload = (raw: any) => {
      let song: any = raw;
      // Unity sometimes sends JSON as a string
      if (typeof song === 'string') {
        try {
          song = JSON.parse(song);
        } catch {
          song = { title: String(raw), audioData: undefined };
        }
      }
      if (!song || typeof song !== 'object') song = {};

      const audioData =
        song.audioData ??
        song.data ?? // legacy key used by some games
        song.audioBase64 ??
        song.base64;

      const createdAt =
        song.createdAt instanceof Date
          ? song.createdAt
          : song.createdAt
            ? new Date(song.createdAt)
            : new Date();

      return {
        ...song,
        title: song.title || 'Untitled Song',
        gameId: song.gameId || gameId,
        audioData,
        createdAt,
      };
    };

    const platformAPI = {
      onGameComplete: async (score: number, cashReward: number = 50, achievements: string[] = []) => {
        try {
          await gameAPI.giveRewards(gameId, { museCash: cashReward, achievements });
        } catch (e) {
          console.warn('[FullScreenGamePlayer] giveRewards failed', e);
        }
      },
      onSongSave: async (songData: any) => {
        try {
          const normalized = normalizeSongPayload(songData);
          await gameAPI.saveSong(normalized);
        } catch (e) {
          console.warn('[FullScreenGamePlayer] saveSong failed', e);
        }
      },
      getUserData: async () => {
        try {
          const [muse, cash] = await Promise.all([gameAPI.getCurrentMuse(), gameAPI.getMuseCash()]);
          return { muse, cash };
        } catch (e) {
          console.warn('[FullScreenGamePlayer] getUserData failed', e);
          return { muse: 0, cash: 0 };
        }
      },
      awardAchievement: async (gid: string, achievementId: string) => {
        try {
          return await gameAPI.awardAchievement(gid, achievementId);
        } catch (e) {
          console.warn('[FullScreenGamePlayer] awardAchievement failed', e);
          return false;
        }
      },
    };

    // Expose in parent window (some games call parent.OverturePlatform).
    // IMPORTANT: don't clobber the richer API from GameAPI.ts; merge instead.
    const existing = (window as any).OverturePlatform;
    const merged =
      existing && typeof existing === 'object'
        ? { ...existing, ...platformAPI }
        : { ...platformAPI };
    (window as any).OverturePlatform = merged;

    // Let postMessage-based games report completions/songs
    const onMessage = async (event: MessageEvent) => {
      if (!mounted) return;
      if (event.data?.type === 'GAME_COMPLETE') {
        await platformAPI.onGameComplete(
          event.data.score || 0,
          event.data.cashReward || 50,
          event.data.achievements || []
        );
      } else if (event.data?.type === 'SONG_SAVE') {
        try {
          const normalized = normalizeSongPayload(event.data.songData);
          const songId = await gameAPI.saveSong(normalized);

          // Match legacy container behavior: respond with SONG_SAVE_RESPONSE
          if (event.source && 'postMessage' in (event.source as any)) {
            (event.source as Window).postMessage(
              { type: 'SONG_SAVE_RESPONSE', success: true, songId },
              '*'
            );
          }
        } catch (e) {
          console.warn('[FullScreenGamePlayer] SONG_SAVE failed', e);
          if (event.source && 'postMessage' in (event.source as any)) {
            (event.source as Window).postMessage(
              {
                type: 'SONG_SAVE_RESPONSE',
                success: false,
                error: e instanceof Error ? e.message : String(e),
              },
              '*'
            );
          }
        }
      } else if (event.data?.type === 'OVERTURE_GAME_EVENT') {
        // New event-based system used by some games
        try {
          await gameAPI.onOvertureGameEvent(event.data.payload);
        } catch (e) {
          console.warn('[FullScreenGamePlayer] onOvertureGameEvent failed', e);
        }
      } else if (event.data?.type === 'REQUEST_MUSE_IMAGE') {
        const img = (userData as any)?.selectedMuse?.img;
        if (img && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'MUSE_IMAGE_RESPONSE',
            payload: { imageUrl: img },
          }, '*');
        }
      }
    };
    window.addEventListener('message', onMessage);

    // Ensure gameAPI has current context (best-effort)
    gameAPI.setCurrentGame(gameId).catch(() => {});

    return () => {
      mounted = false;
      window.removeEventListener('message', onMessage);
    };
  }, [game, gameId, isOgCreators, isPlayerShowdown, userData]);

  const iframeSrc = game?.gameUrl || '';

  if (error) {
    return (
      <div style={{ padding: 24, color: '#111827' }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Failed to launch game</h2>
        <p style={{ marginTop: 8 }}>{error}</p>
      </div>
    );
  }

  if (!game) return null;

  const formatElapsed = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Contain the game so it never gets cut off on odd aspect ratios.
  // Default to a wide 16:9 canvas; this will letterbox/pillarbox as needed.
  const targetAspect = 16 / 9;
  const vw = viewport.w || window.innerWidth;
  const vh = viewport.h || window.innerHeight;
  const viewportAspect = vw > 0 && vh > 0 ? vw / vh : targetAspect;
  const gameW = gameId === 'spot-the-difference' ? vw : viewportAspect > targetAspect ? Math.floor(vh * targetAspect) : vw;
  const gameH = gameId === 'spot-the-difference' ? vh : viewportAspect > targetAspect ? vh : Math.floor(vw / targetAspect);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: isOgCreators ? '#ffffff' : '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 20,
          pointerEvents: 'auto',
        }}
      >
        <button
          type="button"
          onClick={() => {
            // Prefer going "back" if there is history; otherwise fall back to the games menu.
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/app/games');
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            height: 56,
            padding: '0 18px',
            borderRadius: 16,
            border: '3px solid rgba(255,255,255,0.92)',
            background: 'rgba(0, 0, 0, 0.55)',
            color: '#fff',
            fontWeight: 900,
            fontSize: 22,
            cursor: 'pointer',
            boxShadow: '0 10px 22px rgba(0,0,0,0.45)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
          aria-label="Back"
        >
          <span aria-hidden="true" style={{ fontSize: 26, lineHeight: 1 }}>
            ←
          </span>
          Back
        </button>
      </div>

      {/* Timer (top-right) */}
      <div
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 19,
          pointerEvents: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          height: 56,
          padding: '0 18px',
          borderRadius: 16,
          border: '3px solid rgba(255,255,255,0.92)',
          background: 'rgba(0, 0, 0, 0.55)',
          color: '#fff',
          fontWeight: 900,
          fontSize: 22,
          boxShadow: '0 10px 22px rgba(0,0,0,0.45)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
        aria-label="Timer"
      >
        <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1 }}>
          ⏱️
        </span>
        {formatElapsed(elapsedSeconds)}
      </div>

      {isPlayerShowdown ? (
        <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <PlayerShowdown />
        </div>
      ) : isOgCreators ? (
        <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
          <OGCreatorsGame onBackToMenu={() => navigate('/app/games')} />
        </div>
      ) : (
        <div
          style={{
            position: 'relative',
            width: gameW,
            height: gameH,
            maxWidth: '100vw',
            maxHeight: '100vh',
            overflow: 'hidden',
            background: '#000',
          }}
        >
          {iframeLoading && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#000',
              zIndex: 10,
              gap: 12,
            }}>
              <div style={{
                width: 200,
                height: 4,
                borderRadius: 2,
                background: '#333',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  borderRadius: 2,
                  background: '#fff',
                  animation: 'gcs-load 1.5s ease-in-out infinite',
                }} />
              </div>
              <style>{`@keyframes gcs-load { 0% { width: 0%; margin-left: 0; } 50% { width: 60%; margin-left: 20%; } 100% { width: 0%; margin-left: 100%; } }`}</style>
              <span style={{ color: '#888', fontSize: 13 }}>Loading {game.title}...</span>
            </div>
          )}
          <iframe
            ref={iframeRef}
            title={game.title}
            src={iframeSrc}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            allow="autoplay; fullscreen; microphone; camera; midi; encrypted-media"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock allow-orientation-lock allow-popups-to-escape-sandbox"
            onLoad={() => {
              setIframeLoading(false);
              // Same-origin: inject the platform API directly into the game window
              // Delay slightly to give Unity time to initialize (matches old container behavior).
              window.setTimeout(() => {
                try {
                  const w = iframeRef.current?.contentWindow as any;
                  if (w) {
                    w.OverturePlatform = (window as any).OverturePlatform;
                  }
                } catch (e) {
                  // ignore (cross-origin / blocked)
                }

        // Initialize Web MIDI bridge for supported games (e.g., Key Smashers)
        try {
          setupUnityMidiBridge();
        } catch (e) {
          console.warn('[FullScreenGamePlayer] Failed to initialize MIDI bridge', e);
        }
              }, 750);
            }}
          />
        </div>
      )}

      {createPortal(
        <AchievementNotification
          achievement={currentAchievement}
          onComplete={() => setCurrentAchievement(null)}
        />,
        document.body
      )}

      {songNamingData &&
        createPortal(
          <SongNamingModal
            onSave={handleSongNameSave}
            onSkip={handleSongNameSkip}
            audioData={songNamingData.audioUrl}
          />,
          document.body
        )}

      {/* Key Smashers inline piano tools (matches legacy GameContainer behavior) */}
      {isMidiEnabledGame && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 30,
            pointerEvents: 'auto',
            paddingBottom: showInlinePiano ? '1.5rem' : '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'center',
          }}
        >
          {!showInlinePiano && (
            <button
              type="button"
              onClick={() => setShowInlinePiano(true)}
              style={{
                padding: '0.9rem 1.8rem',
                borderRadius: 999,
                border: '3px solid #FFD600',
                background: 'linear-gradient(135deg, #FFF59D, #FFD600)',
                color: '#3E2723',
                fontWeight: 700,
                fontSize: '1.05rem',
                alignSelf: 'center',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                boxShadow: '0 8px 22px rgba(0,0,0,0.35), 0 0 18px rgba(255, 235, 59, 0.8)',
                transform: 'scale(1.02)',
                transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
              }}
            >
              Click me for a piano
            </button>
          )}
          {showInlinePiano && (
            <div style={{ width: '100%' }}>
              <VirtualPianoKeyboard variant="inline" onClose={() => setShowInlinePiano(false)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FullScreenGamePlayer;
